import * as React from 'react'
import {connect} from 'react-redux'
import {CoordinateSystem} from '../../enums/CoordinateSystem'
import {getResolutions, setCookie, getCookie} from '../../helpers'
import {IGeometryEditsDispatcher, IMapDispatcher} from '../../interfaces/dispatchers'
import {IStore} from '../../interfaces/reducers'
import {IMapService} from '../../interfaces/services'
import {Attribution, defaultControls, defaultInteractions, Map, View} from '../../models'
import MousePosition from 'ol/control/MousePosition'
import {ScaleLine} from 'ol/control'
import {config} from '../../reducers/config'
import {MapService} from '../../services'
import OverlayLoader from '../Loading/OverlayLoading'
import proj4 from 'proj4'

export interface DispatchProps {}

export interface OwnProps {
  popupVisible: boolean
  mobile: boolean
  hardLoading: boolean
}

export interface ParentProps {
  mapDispatcher: IMapDispatcher
  geometryDispatcher: IGeometryEditsDispatcher
  cursorPointer?: boolean
  onMapServiceReady: (mapService: IMapService) => void
}

export type Props = DispatchProps & OwnProps & ParentProps

export class MapContainer extends React.Component<Props, {}> {
  map: Map
  mapDiv: HTMLElement
  olEvents: {[key: string]: EventListener} = {}
  isMoving = false
  constructor(props: Props) {
    super(props)
    this.setEventProxies()
    this.createOlMap(props)
  }

  protected mousePositionControl() {
    const firstProjection =
      '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs'
    const secondProjection =
      '+proj=lcc +lat_0=42.6678756833333 +lon_0=25.5 +lat_1=42 +lat_2=43.3333333333333 +x_0=500000 +y_0=4725824.3591 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
    const pos = new MousePosition({
      coordinateFormat: function (coordinate) {
        if (coordinate) {
          const tr = proj4(firstProjection, secondProjection, coordinate)
          return tr[0].toFixed(3) + ', ' + tr[1].toFixed(3)
        }
        return ''
      }
    })
    return pos
  }

  protected scaleControl() {
    const control = new ScaleLine({
      units: 'metric'
    })
    return control
  }

  protected createOlMap(props: Props) {
    let center: [number, number] = [0, 0]
    let zoom = 7
    const conf = config.getConfig()
    const centerX = getCookie('centerX')
    const centerY = getCookie('centerY')

    const zoomCookie = getCookie('Mapzoom')
    if (centerX && centerY) {
      const coords = [centerX, centerY]
      center = [parseFloat(coords[0]), parseFloat(coords[1])]
    } else {
      center = conf.initState.map.center
    }

    if (zoomCookie) {
      zoom = parseFloat(zoomCookie)
    } else {
      zoom = conf.initState.map.zoom
    }

    const attribution = Attribution({
      collapsible: false
    })
    const resolutions: number[] = []
    const allRes = getResolutions()
    for (const zLevel in allRes) {
      resolutions.push(allRes[zLevel])
    }
    const mapOptions = {
      pixelRatio: 1,
      interactions: defaultInteractions({}).extend([]),
      controls: defaultControls({attribution: true, altShiftDragRotate: false, pinchRotate: false}).extend([
        attribution,
        this.scaleControl(),
        this.mousePositionControl()
      ]),
      setProperties: () => ({a: 'b'}),
      view: new View({
        projection: CoordinateSystem.WEB_MERCATOR,
        center: center,
        resolutions: resolutions,
        zoom: zoom,
        maxZoom: 24,
        minZoom: conf.initState.map.minZoom,
        extent: conf.initState.map.initExtent,
        enableRotation: false
      })
    }
    this.map = new Map(mapOptions)
  }

  protected setEventProxies = () => {
    let div: boolean = false
    const olAdditionalEvents = [
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointerout',
      'pointerover',
      'pointercancel',
      'gotpointercapture',
      'lostpointercapture',
      'mousewheel'
    ]
    const f = EventTarget.prototype.addEventListener
    const that = this as MapContainer
    const setAdditionalEvents = (el: HTMLElement, type: string) => {
      if (!div) {
        div = true
        for (let i = 0; i < olAdditionalEvents.length; i++) {
          const e = olAdditionalEvents[i]
          el.addEventListener(e, (ev: Event) => {})
        }
      }
    }
    EventTarget.prototype.addEventListener = function (type: string, fn: EventListener, capture) {
      if (this && fn) {
        this.f = f
        if (this.offsetParent && this.offsetParent.className == 'gm-style') {
          setAdditionalEvents(this, type)
          this.f(
            type,
            (event: Event) => {
              if (type == 'wheel') {
                const e = event as WheelEvent
                fn(event)
                if (!(event as any).handled) {
                  const withNoKeys: boolean = !e.ctrlKey && !e.altKey && !e.metaKey && !e.buttons
                  if (!withNoKeys && that.olEvents[event.type]) {
                    setTimeout(() => {
                      that.olEvents[event.type](event)
                    }, 1)
                  }
                }
              } else {
                fn(event)
                if (that.olEvents[event.type]) {
                  if (that.props.mobile) {
                    that.olEvents[event.type](event)
                  } else {
                    setTimeout(() => {
                      that.olEvents[event.type](event)
                    }, 1)
                  }
                }
              }
            },
            capture
          )
        } else if (this.className && this.className.indexOf('ol-viewport') != -1 && !that.olEvents[type]) {
          that.olEvents[type] = fn
          this.f(
            type,
            (e: Event) => {
              that.olEvents[type](e)
            },
            capture
          )
        } else {
          this.f(type, fn, capture)
        }
      } else {
        f(type, fn, capture)
      }
    }
  }

  protected getMapManager(map: Map, mapDiv: HTMLElement): IMapService {
    return new MapService(config.getConfig(), map, this.props.geometryDispatcher, this.props.mapDispatcher, mapDiv)
  }

  protected registerOlEvents(): void {
    this.map.on('postrender', event => {
      if (this.isMoving) {
        const map: Map = event.target as any
        const view: View = map.getView() as any
        const extent = view.calculateExtent()
        const zoom = view.getZoom() || 1
        const center = view.getCenter() || [0, 0]
        this.props.mapDispatcher.extentChanged(extent, zoom, center[0], center[1])
        setCookie('centerX', center[0].toString(), 365)
        setCookie('centerY', center[1].toString(), 365)
        setCookie('Mapzoom', zoom.toString(), 365)
      }
    })
    this.map.on('ext' as any, event => {
      const map: Map = event.target as any
      const view: View = map.getView() as any
      const extent = view.calculateExtent()
      const zoom = view.getZoom() || 1
      const center = view.getCenter() || [0, 0]
      this.props.mapDispatcher.extentChanged(extent, zoom, center[0], center[1])
    })

    this.map.on('movestart', event => {
      this.isMoving = true
    })
    this.map.on('moveend', event => {
      this.isMoving = false
    })
  }

  componentDidMount() {
    const mapService = this.getMapManager(this.map, this.mapDiv)
    if (this.props.cursorPointer) {
      mapService.startCursorPointer()
    }
    mapService.startClickEvent()

    this.registerOlEvents()

    // Add map manager to Redux before notifying parent so getMapService() is available
    this.props.mapDispatcher.addMapManager(mapService)
    this.props.onMapServiceReady(mapService)
    setTimeout(() => {
      this.props.onMapServiceReady(mapService)
      const appConfig = config.getConfig()
      if (appConfig.initState.map.basemap) {
        this.props.mapDispatcher.setBasemap(appConfig.initState.map.basemap)
      }
    }, 30)

    this.map.setTarget(this.mapDiv)
  }
  render() {
    return (
      <div style={{height: '100%', position: 'relative', overflow: 'hidden', maxHeight: '100%', maxWidth: '100%'}}>
        <div
          style={{
            width: '100%',
            height: '100%',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            position: 'absolute',
            display: this.props.hardLoading ? 'block' : 'none',
            backgroundColor: 'white'
          }}
        >
          <OverlayLoader size="100px" show={true} />
        </div>
        <div
          id="ol-map"
          style={{
            position: 'absolute',
            zIndex: 1,
            width: '100%',
            height: '100%'
          }}
          ref={element => (this.mapDiv = element!)}
        ></div>
        <div
          className="ol-attribution ol-unselectable ol-control ol-uncollapsible"
          style={{
            zIndex: 1,
            left: 0,
            borderRadius: '0 4px 0 0',
            right: 'unset'
          }}
        >
          <ul>
            <li>
              <a href="https://terms.alfagis.bg/" target="_blank" rel="noopener noreferrer">
                Лицензно споразомение
              </a>
            </li>
          </ul>
          <ul>Version v:{process.env.VERSION}</ul>
          <ul id="measure"></ul>
        </div>
      </div>
    )
  }
}

export const mapStateToProps = (state: IStore) => {
  return {
    popupVisible: state.map.popup.show,
    mobile: state.application.mobile,
    hardLoading: state.map.hardLoading
  }
}

export default connect<OwnProps, DispatchProps, ParentProps>(mapStateToProps, {})(MapContainer)
